import { useState, useEffect } from 'react'
import {
  Search,
  RefreshCw,
  ShoppingCart,
  User,
  Calendar,
  Check,
  Loader2,
  Clock,
  MapPin,
  Package,
  Truck,
  X,
} from 'lucide-react'
import {
  useHeldOrders,
  useRecentPaidOrders,
  useEcomOrders,
  useDeliveryOrders,
  useSetDeliveryStatus,
  useSearchOrders,
  RECEIPT_STATUS,
  DELIVERY_STATUS,
  DELIVERY_STATUS_LABEL,
  type DeliveryStatus,
} from '@/api/receipts'
import { v2, v2api } from '@/api/v2'
import { usePOSConfig } from '@/api/config'
import { useUIStore } from '@/stores/uiStore'
import { useDebounce } from '@/hooks'
import { formatCurrency } from '@/lib/utils'
import { OrderDetailModal } from './OrderDetailModal'
import { AddToDeliveryRunModal } from '@/components/modals'
import { DeliveryRunsPanel } from '@/components/fulfillment/DeliveryRunsPanel'
import { FulfillmentTypePill } from '@/components/fulfillment/FulfillmentTypePill'
import { fulfillmentType } from '@/lib/fulfillment'
import type { Customer } from '@/types'

type OrderFilter = 'held' | 'recent' | 'fulfillment' | 'search'

interface OrdersTabProps {
  onOpenCustomer: (customer: Customer) => void
}

export function OrdersTab({ onOpenCustomer }: OrdersTabProps) {
  const [filter, setFilter] = useState<OrderFilter>('recent')
  // Sub-filter within the Pickup/Delivery tab.
  const [fulfillmentTypeFilter, setFulfillmentTypeFilter] = useState<
    'all' | 'pickup' | 'delivery'
  >('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchAllTime, setSearchAllTime] = useState(false)
  const debouncedSearch = useDebounce(searchQuery, 500)
  // Holds both the receipt id and — when the search join found one — the
  // linked transaction id. The modal prefers the transaction path because
  // `SA_JournalTransaction` is the richer source of truth (line-item brand/
  // SKU/unit, full payment details). Held orders have no transaction yet,
  // so we fall back to the receipt path.
  const [openOrder, setOpenOrder] = useState<
    { receiptId: number; transactionId: number | null; number: string } | null
  >(null)

  // Deliveries tab: receipt ids checked for "Add to Delivery Run". Orders
  // already on a run aren't selectable (their checkbox is disabled), so this
  // set only ever holds unassigned delivery orders.
  const [selectedDeliveryIds, setSelectedDeliveryIds] = useState<Set<number>>(
    new Set(),
  )
  const [showAddRunModal, setShowAddRunModal] = useState(false)
  // Slide-over runs rail (M1) — view active runs + per-run progress map
  // without leaving the Orders tab.
  const [runsPanelOpen, setRunsPanelOpen] = useState(false)

  const { showToast } = useUIStore()

  const toggleDeliverySelect = (receiptId: number) => {
    setSelectedDeliveryIds((prev) => {
      const next = new Set(prev)
      if (next.has(receiptId)) next.delete(receiptId)
      else next.add(receiptId)
      return next
    })
  }

  // Switch to/from search mode based on the debounced query so we don't flicker
  // tabs on every keystroke. Gate on the immediate `searchQuery` too — when
  // the cashier clicks a non-search tab, `handleTabChange` clears the input
  // synchronously but `debouncedSearch` lags by the debounce window, and
  // without the immediate-value check the effect would bounce filter back
  // to 'search' until the debounced value catches up.
  useEffect(() => {
    if (searchQuery.length >= 3 && debouncedSearch.length >= 3) {
      setFilter('search')
    } else if (filter === 'search') {
      setFilter('recent')
      // Reset to the fast 90-day window so the next search starts cheap.
      // The explicit reset paths (tab click, X clear) already do this, but
      // editing the query down to <3 chars is an implicit exit that would
      // otherwise leave the next search stuck in all-time mode.
      setSearchAllTime(false)
    }
  }, [searchQuery, debouncedSearch, filter])

  // Fetch data based on active tab
  const { data: heldOrders, isLoading: loadingHeld, refetch: refetchHeld } = useHeldOrders()
  const { data: recentOrders, isLoading: loadingRecent, refetch: refetchRecent } = useRecentPaidOrders()
  const { data: ecomOrders, isLoading: loadingEcom, refetch: refetchEcom } = useEcomOrders()
  // Deliveries are gated on the store's "Allow Delivery" setting
  // (kiosk.delivery.allow_delivery). When off, the sub-tab is hidden and the
  // fetch is skipped.
  const { data: posConfig } = usePOSConfig()
  const allowDelivery = posConfig?.posAllowDelivery ?? false
  const { data: deliveryOrders, isLoading: loadingDelivery, refetch: refetchDelivery } = useDeliveryOrders(allowDelivery)
  const setDeliveryStatus = useSetDeliveryStatus()

  const handleSetDeliveryStatus = (receiptId: number, status: DeliveryStatus) => {
    setDeliveryStatus.mutate(
      { receiptId, status },
      {
        onSuccess: () => {
          showToast({
            type: 'success',
            title: 'Delivery updated',
            message: DELIVERY_STATUS_LABEL[status] ?? status,
          })
        },
        onError: (err) => {
          showToast({
            type: 'error',
            title: 'Could not update delivery',
            message: err instanceof Error ? err.message : undefined,
          })
        },
      },
    )
  }
  // Gate at 3 chars (not the hook's internal 2) — receipt search is a
  // BE seq-scan with no trigram index, so 2-char queries balloon the
  // candidate set. CommandPalette uses the same hook at 2 chars.
  const { data: searchResults, isLoading: loadingSearch } = useSearchOrders(
    debouncedSearch.length >= 3 ? debouncedSearch : '',
    { allTime: searchAllTime },
  )

  // Get current data and loading state based on filter
  const getCurrentData = () => {
    switch (filter) {
      case 'held':
        return { data: heldOrders, isLoading: loadingHeld }
      case 'search':
        return { data: searchResults, isLoading: loadingSearch }
      default:
        return { data: recentOrders, isLoading: loadingRecent }
    }
  }

  // Fulfillment is derived from two queries (below); the rest come straight
  // from getCurrentData.
  const { data: baseOrders, isLoading: baseLoading } = getCurrentData()

  // Refund detection — paid receipt created via copy_with_refund. Matches
  // OrderCard.isRefund (any of is_return / src_id / return_receipt_id).
  // The BE search endpoint with `kiosk: true` currently returns refunds
  // of kiosk-originated sales, which aren't actually kiosk orders the
  // cashier needs to fulfill. Filter them out here.
  const isRefundReceipt = (order: any): boolean =>
    order?.is_return === true
    || Boolean(order?.src_id)
    || Boolean(order?.return_receipt_id)

  // Pending held orders = recallable held sales, with refund receipts
  // stripped. Mirrors the held filter in `filteredOrders` so the tab
  // count matches the visible list.
  const pendingHeldOrders = heldOrders?.filter(
    (order: any) => !isRefundReceipt(order),
  )
  // The fulfillment list shows EVERY delivery order the BE returns (delivered
  // ones stay for reference). The count badge scopes to the still-outstanding
  // ones so a finished order doesn't inflate the "work waiting" number.
  const isTerminalDelivery = (order: any): boolean =>
    order?.delivery_status === DELIVERY_STATUS.DELIVERED
    || order?.delivery_status === DELIVERY_STATUS.PICKUP_AT_STORE
  const pendingDeliveryOrders = deliveryOrders?.filter(
    (order: any) => !isRefundReceipt(order) && !isTerminalDelivery(order),
  )

  // Unified Fulfillment queue = active delivery orders (the delivery query
  // keeps in-transit orders regardless of kiosk_status — so a delivery whose
  // kiosk_status already reads "Completed" isn't dropped) + pending PICKUP
  // orders (non-delivery ecom orders). Pickup = is_delivery !== true, NOT
  // `=== false`: at delivery-disabled stores the BE returns is_delivery as
  // null/undefined, and `=== false` silently dropped every pickup order. The
  // two sets stay disjoint (delivery orders are is_delivery === true), so no
  // dedupe is needed. Delivery orders are run-eligible; pickup orders appear
  // for visibility with a Pickup pill.
  const pickupOrders = (ecomOrders ?? []).filter(
    (order) =>
      order.is_delivery !== true
      && order.kiosk_status !== 'Completed'
      && !isRefundReceipt(order),
  )
  const fulfillmentOrders = [
    ...(deliveryOrders ?? []).filter((order) => !isRefundReceipt(order)),
    ...pickupOrders,
  ]
  const pendingFulfillmentOrders = [
    ...(pendingDeliveryOrders ?? []),
    ...pickupOrders,
  ]

  const typeFilteredFulfillment =
    fulfillmentTypeFilter === 'all'
      ? fulfillmentOrders
      : fulfillmentOrders.filter((o) => fulfillmentType(o) === fulfillmentTypeFilter)
  const orders =
    filter === 'fulfillment' ? typeFilteredFulfillment : baseOrders
  const isLoading =
    filter === 'fulfillment' ? loadingDelivery || loadingEcom : baseLoading

  // For search mode, use search results directly; otherwise filter locally
  const filteredOrders = filter === 'search'
    ? orders || []
    : orders?.filter((order: any) => {
        // Fulfillment is pre-filtered above (refunds dropped, terminal scoping
        // handled per source), so only Held needs its own row filter here.
        if (filter === 'held') {
          // Hide refund receipts — Held is for in-progress sales saved
          // for later. The BE's on_hold=true search currently returns
          // paid refund receipts (likely transitional status during the
          // refund flow), which aren't anything the cashier can recall
          // and resume.
          if (isRefundReceipt(order)) return false
        }
        if (!searchQuery) return true
        const query = searchQuery.toLowerCase()
        const customerName = order.customer?.display_name || ''
        return (
          order.number?.toLowerCase().includes(query) ||
          customerName.toLowerCase().includes(query) ||
          order.id.toString().includes(query)
        )
      }) || []

  const handleRefresh = () => {
    switch (filter) {
      case 'held': refetchHeld(); break
      case 'fulfillment': refetchEcom(); refetchDelivery(); break
      default: refetchRecent(); break
    }
  }

  const handleTabChange = (newFilter: OrderFilter) => {
    setFilter(newFilter)
    setSearchQuery('') // Clear search when changing tabs
    setSearchAllTime(false) // Reset to fast 90-day search for the next query
    setSelectedDeliveryIds(new Set()) // Drop any delivery-run selection
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        {/* Search + refresh */}
        <div className="flex items-center gap-2 mb-4">
          <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by order #, customer name, or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery('')
                setFilter('recent')
                setSearchAllTime(false)
              }}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          </div>
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="flex-none p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Filter Tabs: Recent / Fulfillment / On Hold. Fulfillment merges the
            old Ecom + Delivery tabs (both are ecom orders) — delivery + pickup
            in one queue, delivery orders run-eligible. */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <button
            onClick={() => handleTabChange('recent')}
            disabled={filter === 'search' && searchQuery.length >= 3}
            className={`flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              filter === 'recent'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-50'
            }`}
          >
            <Calendar className="w-4 h-4" />
            <span>Recent</span>
            {recentOrders && recentOrders.length > 0 && filter !== 'search' && (
              <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-700 rounded-full">
                {recentOrders.length}
              </span>
            )}
          </button>

          <button
            onClick={() => handleTabChange('fulfillment')}
            disabled={filter === 'search' && searchQuery.length >= 3}
            className={`flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              filter === 'fulfillment'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-50'
            }`}
          >
            <Package className="w-4 h-4" />
            <span>{allowDelivery ? 'Pickup/Delivery' : 'Pickup'}</span>
            {pendingFulfillmentOrders.length > 0 && filter !== 'search' && (
              <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-700 rounded-full">
                {pendingFulfillmentOrders.length}
              </span>
            )}
          </button>

          <button
            onClick={() => handleTabChange('held')}
            disabled={filter === 'search' && searchQuery.length >= 3}
            className={`flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              filter === 'held'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-50'
            }`}
          >
            <Clock className="w-4 h-4" />
            <span>On Hold</span>
            {pendingHeldOrders && pendingHeldOrders.length > 0 && filter !== 'search' && (
              <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-700 rounded-full">
                {pendingHeldOrders.length}
              </span>
            )}
          </button>
        </div>

        {/* Active Search Indicator */}
        {filter === 'search' && searchQuery.length >= 3 && (
          <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded-md text-xs text-green-700 flex items-center justify-between gap-2">
            <p>
              Searching {searchAllTime ? 'all-time' : 'last 90 days'} for:{' '}
              <span className="font-semibold">{searchQuery}</span>
            </p>
            <button
              onClick={() => setSearchAllTime((v) => !v)}
              className="font-semibold underline hover:text-green-900 cursor-pointer whitespace-nowrap"
            >
              {searchAllTime ? 'Last 90 days' : 'Search all time'}
            </button>
          </div>
        )}


      </div>

      {/* Deliveries-tab action bar — group selected orders into a run, and
          jump to the /deliveries board to manage/run them. */}
      {filter === 'fulfillment' && allowDelivery && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 border-b border-gray-200 bg-gray-50">
          {/* Left: Pickup/Delivery sub-filter pills */}
          <div className="flex items-center gap-2">
            {(['all', 'pickup', 'delivery'] as const).map((t) => {
              const count =
                t === 'all'
                  ? fulfillmentOrders.length
                  : fulfillmentOrders.filter((o) => fulfillmentType(o) === t)
                      .length
              return (
                <button
                  key={t}
                  onClick={() => setFulfillmentTypeFilter(t)}
                  className={`flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-full capitalize transition-colors ${
                    fulfillmentTypeFilter === t
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                >
                  {t}
                  <span className="text-xs opacity-70">{count}</span>
                </button>
              )
            })}
          </div>
          {/* Right: runs rail + selection + add */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setRunsPanelOpen(true)}
              className="flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-900 cursor-pointer"
            >
              <Truck className="w-4 h-4" />
              Delivery runs
            </button>
            {selectedDeliveryIds.size > 0 && (
              <span className="text-sm font-semibold text-blue-700">
                {selectedDeliveryIds.size} selected
              </span>
            )}
            <button
              onClick={() => setShowAddRunModal(true)}
              disabled={selectedDeliveryIds.size === 0}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-semibold rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
            >
              Add to Delivery Run
            </button>
          </div>
        </div>
      )}

      {/* Orders List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <ShoppingCart className="w-12 h-12 mb-4 text-gray-300" />
            <p className="text-lg font-medium mb-2">
              {searchQuery ? 'No results found' : 'No orders found'}
            </p>
            <p className="text-sm">
              {searchQuery
                ? 'Try a different search term'
                : filter === 'held'
                  ? 'Use "Hold Receipt" to save a transaction'
                  : filter === 'fulfillment'
                    ? 'No online orders waiting for fulfillment'
                    : 'No orders in this time period'
              }
            </p>
            {/* When the 90-day search came up empty, surface the all-time
                fallback inline — the green banner up top has the toggle
                too, but offering it here closes the loop on "no results"
                without making the cashier hunt for it. */}
            {filter === 'search' && !searchAllTime && (
              <button
                onClick={() => setSearchAllTime(true)}
                className="mt-3 text-sm font-semibold text-green-700 underline hover:text-green-900 cursor-pointer"
              >
                Search all time
              </button>
            )}
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {filteredOrders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                hideStatusBadge={filter === 'fulfillment'}
                showDeliveryActions={filter === 'fulfillment' && order.is_delivery === true}
                onSetDeliveryStatus={handleSetDeliveryStatus}
                deliveryActionPending={setDeliveryStatus.isPending}
                selectable={filter === 'fulfillment' && allowDelivery && order.is_delivery === true}
                showFulfillmentPill={filter === 'fulfillment'}
                selected={selectedDeliveryIds.has(order.id)}
                onToggleSelect={() => toggleDeliverySelect(order.id)}
                onOpen={() => {
                  const txId = Number(order.transaction_id)
                  setOpenOrder({
                    receiptId: order.id,
                    transactionId: Number.isFinite(txId) && txId > 0 ? txId : null,
                    number: order.number || String(order.id),
                  })
                }}
              />
            ))}
          </div>
        )}
      </div>

      {openOrder && (
        <OrderDetailModal
          transactionId={openOrder.transactionId ?? undefined}
          receiptId={openOrder.transactionId ? undefined : openOrder.receiptId}
          receiptNumber={openOrder.number}
          hideAddActions
          onClose={() => setOpenOrder(null)}
          onOpenCustomer={(customer) => {
            setOpenOrder(null)
            onOpenCustomer(customer)
          }}
          onOpenReceipt={async ({ receiptId, receiptNumber }) => {
            // Jump from a refund's "Source:" link back to the original
            // sale. Prefer the numeric id when the BE provided one;
            // otherwise resolve the receipt number via the search
            // endpoint (the v2 receipt response sometimes ships only
            // `src_number`, no `src_id`).
            if (receiptId != null) {
              setOpenOrder({
                receiptId,
                transactionId: null,
                number: receiptNumber ?? '',
              })
              return
            }
            if (!receiptNumber) return
            try {
              const data = await v2api.get<unknown>(
                v2('/receipts/search'),
                { q: receiptNumber, any: true, limit: 5 },
              )
              const list = Array.isArray(data)
                ? (data as Array<Record<string, unknown>>)
                : ((data as { receipts?: Array<Record<string, unknown>> })?.receipts ?? [])
              const match = list.find(
                (r) => typeof r.number === 'string' && r.number === receiptNumber,
              )
              const resolvedId = match && typeof match.id === 'number' ? match.id : null
              if (resolvedId == null) {
                showToast({
                  type: 'error',
                  title: 'Source receipt not found',
                  message: `Could not locate ${receiptNumber}.`,
                })
                return
              }
              setOpenOrder({
                receiptId: resolvedId,
                transactionId: null,
                number: receiptNumber,
              })
            } catch {
              showToast({
                type: 'error',
                title: 'Failed to open source',
                message: 'Try opening the receipt from the orders list.',
              })
            }
          }}
        />
      )}

      {showAddRunModal && (
        <AddToDeliveryRunModal
          orders={(deliveryOrders ?? []).filter((o) =>
            selectedDeliveryIds.has(o.id),
          )}
          onClose={() => setShowAddRunModal(false)}
          onAdded={() => {
            setSelectedDeliveryIds(new Set())
            refetchDelivery()
          }}
        />
      )}

      {runsPanelOpen && (
        <DeliveryRunsPanel onClose={() => setRunsPanelOpen(false)} />
      )}

    </div>
  )
}

interface OrderCardProps {
  order: any
  hideStatusBadge?: boolean
  showFulfillmentPill?: boolean
  showDeliveryActions?: boolean
  onSetDeliveryStatus?: (receiptId: number, status: DeliveryStatus) => void
  deliveryActionPending?: boolean
  // Deliveries tab: show a checkbox to pick the order for a delivery run.
  selectable?: boolean
  selected?: boolean
  onToggleSelect?: () => void
  onOpen: () => void
}

// Badge tint for a delivery order's fulfillment state. Mirrors
// DELIVERY_STATUS on the BE.
function deliveryStatusBadgeClass(status: string): string {
  switch (status) {
    case DELIVERY_STATUS.DELIVERED:
      return 'bg-green-100 text-green-700'
    case DELIVERY_STATUS.OUT_FOR_DELIVERY:
      return 'bg-blue-100 text-blue-700'
    case DELIVERY_STATUS.DELIVERY_FAILED:
      return 'bg-red-100 text-red-700'
    case DELIVERY_STATUS.PICKUP_AT_STORE:
      return 'bg-amber-100 text-amber-800'
    case DELIVERY_STATUS.PREPARING:
      return 'bg-indigo-100 text-indigo-700'
    default:
      return 'bg-gray-100 text-gray-700'
  }
}

// The actions a clerk can take given the order's current delivery_status.
// "Out for Delivery Again" (a retry after a failure) is just another
// out_for_delivery transition — the BE history log makes the retry visible.
// NB: there is intentionally no "Delivered" action. Delivery completion
// happens through the POS payment flow — the cashier opens the order, clicks
// Complete, and takes payment; a fully-paid delivery receipt is marked
// delivered by the backend. These buttons only move the in-transit state.
function deliveryActionsFor(
  status: string,
): Array<{ label: string; status: DeliveryStatus; tone: string }> {
  const failed = { label: 'Failed', status: DELIVERY_STATUS.DELIVERY_FAILED, tone: 'bg-red-600 hover:bg-red-700 text-white' }
  const pickup = { label: 'Pickup at Store', status: DELIVERY_STATUS.PICKUP_AT_STORE, tone: 'bg-gray-100 hover:bg-gray-200 text-gray-700' }
  const out = { label: 'Out for Delivery', status: DELIVERY_STATUS.OUT_FOR_DELIVERY, tone: 'bg-blue-600 hover:bg-blue-700 text-white' }
  const outAgain = { label: 'Out for Delivery Again', status: DELIVERY_STATUS.OUT_FOR_DELIVERY, tone: 'bg-blue-600 hover:bg-blue-700 text-white' }

  switch (status) {
    case DELIVERY_STATUS.OUT_FOR_DELIVERY:
      return [failed, pickup]
    case DELIVERY_STATUS.DELIVERY_FAILED:
      return [outAgain, pickup]
    default: // none / preparing
      return [out, pickup]
  }
}

// Color-code the BE search endpoint's stringified `kiosk_status` so the
// "Ready" pill is visually distinct from the still-being-prepared
// states. Mirrors the numeric KioskReceiptStatus enum at
// project/frontend/models/pos.py — search serializes 0/1/2/3 to
// 'New'/'Preparing'/'Ready'/'Completed' (frontend.py:2770).
function kioskStatusBadgeClass(value: string): string {
  switch (value) {
    case 'Ready':
      return 'bg-green-100 text-green-700'
    case 'Preparing':
      return 'bg-blue-100 text-blue-700'
    case 'New':
      return 'bg-yellow-100 text-yellow-800'
    case 'Completed':
      return 'bg-gray-200 text-gray-700'
    default:
      return 'bg-orange-100 text-orange-700'
  }
}

function OrderCard({
  order,
  hideStatusBadge,
  showFulfillmentPill,
  showDeliveryActions,
  onSetDeliveryStatus,
  deliveryActionPending,
  selectable,
  selected,
  onToggleSelect,
  onOpen,
}: OrderCardProps) {
  // An order already on a delivery run can't be selected again; we show its
  // run name and disable the checkbox. `delivery_run` comes from the search
  // payload (search.py) as { id, name } | null.
  const assignedRun = order?.delivery_run as { id: number; name: string } | null
  // A delivered order is done — it can't be added to a (future) run either,
  // so its checkbox is disabled too.
  const isDelivered = order?.delivery_status === DELIVERY_STATUS.DELIVERED
  // pickup_at_store is terminal too — a "switch to in-store pickup" order
  // isn't being delivered, so it must not be selectable for a delivery run
  // (matches isTerminalDelivery, which the pending count uses).
  const isPickupAtStore = order?.delivery_status === DELIVERY_STATUS.PICKUP_AT_STORE
  const selectDisabled = !!assignedRun || isDelivered || isPickupAtStore
  // Refund detection — a paid receipt that was created via copy_with_refund.
  // Backend marks these with `is_return: true` and a `src_id` linking back
  // to the original sale. Either signal qualifies; some legacy responses
  // have one but not the other.
  const isRefund =
    order?.is_return === true
    || Boolean(order?.src_id)
    || Boolean(order?.return_receipt_id)

  const getStatusBadge = (status: number) => {
    // Status values mirror waio-portal PosReceiptStatus. Refunds aren't
    // a status — they're paid receipts with `is_return: true`. Detect
    // via the order's `is_return` / `src_id` first, fall through to the
    // status-code switch otherwise.
    if (isRefund) {
      return <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">Refund</span>
    }
    switch (Number(status)) {
      case RECEIPT_STATUS.PAID:
        return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Paid</span>
      case RECEIPT_STATUS.PAID_ONLINE:
        // Prepaid card order awaiting pickup — the clerk must see at a
        // glance that no payment is due at the counter.
        return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Paid</span>
      case RECEIPT_STATUS.PAYING:
        return <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 rounded-full">Paying</span>
      case RECEIPT_STATUS.HOLD:
        return <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">On Hold</span>
      case RECEIPT_STATUS.NEW:
        return <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">Pending</span>
      case RECEIPT_STATUS.CANCELED:
        return <span className="px-2 py-1 text-xs font-medium bg-gray-200 text-gray-700 rounded-full">Canceled</span>
      case RECEIPT_STATUS.DELETED:
        return <span className="px-2 py-1 text-xs font-medium bg-gray-200 text-gray-700 rounded-full">Deleted</span>
      default:
        return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">Unknown</span>
    }
  }

  // On the Deliveries tab the card IS the run-selection control: tapping a
  // selectable order toggles it (clear blue ring + check), so building a run is
  // an obvious "tap the orders you want" gesture rather than a stray checkbox.
  // Tapping the card always opens the detail modal — the cashier needs to see
  // what's in the order. Run-eligible orders also show a checkbox to select
  // them for a run; the checkbox stops propagation so it doesn't open the modal.
  const canSelect = selectable && !selectDisabled
  return (
    <div
      onClick={onOpen}
      className={`rounded-lg p-4 border transition-all cursor-pointer ${
        selected
          ? 'bg-blue-50 border-blue-500 ring-2 ring-blue-500'
          : 'bg-white border-gray-200 hover:shadow-md hover:border-gray-300'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          {canSelect && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onToggleSelect?.()
              }}
              aria-label={selected ? 'Deselect order' : 'Select order for a run'}
              aria-pressed={selected}
              className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 cursor-pointer transition-colors ${
                selected
                  ? 'bg-blue-500 border-blue-500'
                  : 'bg-white border-gray-300 hover:border-gray-400'
              }`}
            >
              {selected && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
            </button>
          )}
          {/* Number on its own line with the badges wrapping below on mobile
              (narrow main area); inline on md+ (wide desktop main area). The
              flex-wrap group + bounded width stops badges clipping past the
              card edge when there are several (Online / Delivery / status). */}
          <div className="flex flex-col md:flex-row md:items-center gap-1.5 md:gap-2 min-w-0 flex-1">
            <span className="font-semibold text-gray-900 md:shrink-0">
              #{order.number || order.id}
            </span>
            <div className="flex flex-wrap items-center gap-1.5">
              {assignedRun && (
                <span className="px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-full">
                  {assignedRun.name}
                </span>
              )}
              {/* Drop the "Online" badge when a Pickup/Delivery indicator is
                  already shown (the fulfillment pill, or the standalone
                  Delivery badge below) — it's redundant there. */}
              {order.order_type === 'Online' && !showFulfillmentPill && !order.is_delivery && (
                <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                  Online
                </span>
              )}
              {showFulfillmentPill ? (
                <FulfillmentTypePill order={order} />
              ) : order.is_delivery ? (
                <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
                  Delivery
                </span>
              ) : null}
              {typeof order.kiosk_status === 'string' && order.kiosk_status && (
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${kioskStatusBadgeClass(order.kiosk_status)}`}>
                  {order.kiosk_status}
                </span>
              )}
              {/* Delivery fulfillment state — shown on the Ecom and Deliveries
                  tabs so the clerk sees where in the delivery leg an order is.
                  Hidden when 'none' (not yet started) to avoid noise. */}
              {order.is_delivery
                && typeof order.delivery_status === 'string'
                && order.delivery_status
                && order.delivery_status !== DELIVERY_STATUS.NONE && (
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${deliveryStatusBadgeClass(order.delivery_status)}`}>
                  {DELIVERY_STATUS_LABEL[order.delivery_status] ?? order.delivery_status}
                </span>
              )}
              {/* The fulfillment (Pickup/Delivery) view hides the status
                  badge as noise -- EXCEPT for prepaid online orders,
                  where "Paid" is the one thing the clerk must see. */}
              {(!hideStatusBadge
                || Number(order.status) === RECEIPT_STATUS.PAID_ONLINE)
                && getStatusBadge(order.status)}
            </div>
          </div>
        </div>
      </div>

      {/* Order Details */}
      <div className="flex items-start justify-between gap-4 text-sm">
        <div className="space-y-1.5 min-w-0 flex-1">
          {order.customer && (
            <div className="flex items-center text-gray-600">
              <User className="w-4 h-4 mr-2 shrink-0" />
              <span className="truncate">{order.customer.display_name}</span>
            </div>
          )}
          <div className="flex items-center text-gray-600">
            <Calendar className="w-4 h-4 mr-2 shrink-0" />
            <span>{new Date(order.timestamp).toLocaleString()}</span>
          </div>
          {order.is_delivery && order.address && (
            <div className="flex items-center text-gray-600">
              <MapPin className="w-4 h-4 mr-2 shrink-0" />
              <span className="truncate">
                {[order.address, order.city, order.province]
                  .filter(Boolean)
                  .join(', ')}
              </span>
            </div>
          )}
        </div>
        {/* Refunds render in red with an explicit minus sign so they're
            visually distinct from paid sales in the recent-orders list.
            Some refund responses have a positive `totals.total` (legacy
            quirk) and some negative — we coerce to abs and apply the sign
            from the isRefund flag so the display is consistent. */}
        <span
          className={`font-semibold tabular-nums shrink-0 self-center ${
            isRefund ? 'text-red-600' : 'text-gray-900'
          }`}
        >
          {isRefund ? '-' : ''}
          {formatCurrency(Math.abs(Number(order.totals.total ?? 0)))}
        </span>
      </div>

      {/* Deliveries-tab actions — advance the order's delivery_status.
          stopPropagation so tapping a button doesn't also open the detail
          modal. Buttons are contextual to the current status. */}
      {showDeliveryActions && onSetDeliveryStatus && (
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
          {deliveryActionsFor(order.delivery_status ?? DELIVERY_STATUS.NONE).map(
            (action) => (
              <button
                key={action.label}
                disabled={deliveryActionPending}
                onClick={(e) => {
                  e.stopPropagation()
                  onSetDeliveryStatus(order.id, action.status)
                }}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors disabled:opacity-50 cursor-pointer ${action.tone}`}
              >
                {action.label}
              </button>
            ),
          )}
        </div>
      )}
    </div>
  )
}
